<template>
    <div class="content mk-aff-payouts">
        <base-header class="pb-6">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">{{ $t("marketing.menu.affiliate_payouts") }}</h6>
                    <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
                        <route-breadcrumb></route-breadcrumb>
                    </nav>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
                <template slot="header">
                    <div class="d-flex flex-wrap align-items-center justify-content-between" style="gap: 12px;">
                        <el-radio-group v-model="status" size="small" data-testid="aff-payout-status" @change="reload(1)">
                            <el-radio-button label="">{{ $t("marketing.affiliate.all") }}</el-radio-button>
                            <el-radio-button v-for="s in statuses" :key="s" :label="s">{{ tk("marketing.affiliate.payout.status_", s) }}</el-radio-button>
                        </el-radio-group>
                        <base-button v-if="canWrite && !schemaMissing" type="primary" size="sm" data-testid="aff-payout-create" @click="openCreate">
                            <i class="fa fa-plus"></i> {{ $t("marketing.affiliate.payout.create") }}
                        </base-button>
                    </div>
                </template>

                <div v-if="schemaMissing" class="px-4 pb-3">
                    <el-alert type="error" :closable="false" show-icon :title="$t('marketing.schema_missing')" data-testid="schema-missing"></el-alert>
                </div>

                <el-table v-loading="loading" :data="rows" class="aff-table bon-table bon-tight" row-key="id" header-row-class-name="thead-light" :empty-text="$t('marketing.affiliate.empty')"
                    row-class-name="mk-click-row" data-testid="aff-payout-table" @row-click="open">
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.code')" width="110">
                        <template v-slot="{ row }"><span class="font-weight-bold">{{ row.code }}</span></template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.status')" width="95">
                        <template v-slot="{ row }"><el-tag size="small" :type="statusTag(row.status)">{{ tk("marketing.affiliate.payout.status_", row.status) }}</el-tag></template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.cutoff')" width="100">
                        <template v-slot="{ row }">{{ dateOnly(row.cutoff_at) }}</template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.people')" width="100" align="right" prop="item_count"></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.paid_of')" width="80" align="center">
                        <template v-slot="{ row }">{{ row.paid_count }}/{{ row.item_count }}</template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.gross')" width="104" align="right"><template v-slot="{ row }">{{ money(row.total_gross) }}</template></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.wht')" width="138" align="right"><template v-slot="{ row }">{{ money(row.total_wht) }}</template></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.net')" width="104" align="right"><template v-slot="{ row }"><b>{{ money(row.total_net) }}</b></template></el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.created')" min-width="140">
                        <template v-slot="{ row }">
                            <div class="small">{{ row.created_by_name || "-" }}</div>
                            <div class="small text-muted">{{ fmt(row.created_at) }}</div>
                        </template>
                    </el-table-column>
                    <el-table-column class-name="bon-tight" :label="$t('marketing.affiliate.payout.note')" min-width="110">
                        <template v-slot="{ row }"><small>{{ row.note || "-" }}</small></template>
                    </el-table-column>
                </el-table>
                <div slot="footer" class="d-flex justify-content-end px-3">
                    <el-pagination layout="total, prev, pager, next" :total="total" :page-size="20" :current-page="page" @current-change="reload"></el-pagination>
                </div>
            </card>
        </div>

        <!-- create round (§5.5): cutoff → live preview → note → create (draft) -->
        <el-dialog :title="$t('marketing.affiliate.payout.create')" :visible.sync="createOpen" width="560px" append-to-body :close-on-click-modal="false">
            <div data-testid="aff-payout-create-dialog">
                <div class="form-group">
                    <label class="form-control-label">{{ $t("marketing.affiliate.payout.cutoff") }}</label>
                    <el-date-picker v-model="cutoff" type="date" value-format="yyyy-MM-dd" format="yyyy-MM-dd" class="w-100" :clearable="false" :picker-options="pickerOptions" data-testid="aff-payout-cutoff" @change="loadPreview"></el-date-picker>
                </div>
                <div v-loading="previewLoading" class="aff-preview mb-3" data-testid="aff-payout-preview">
                    <template v-if="preview">
                        <p class="font-weight-bold mb-1">{{ $t("marketing.affiliate.payout.preview_eligible", { n: preview.eligible_count }) }}</p>
                        <div class="d-flex flex-wrap mb-2" style="gap: 16px;">
                            <span>{{ $t("marketing.affiliate.payout.gross") }}: <b>{{ money(preview.total_gross) }}</b></span>
                            <span>{{ $t("marketing.affiliate.payout.wht") }}: <b>{{ money(preview.total_wht) }}</b></span>
                            <span>{{ $t("marketing.affiliate.payout.net") }}: <b>{{ money(preview.total_net) }}</b></span>
                        </div>
                        <ul class="small text-muted mb-0 pl-3">
                            <li>{{ $t("marketing.affiliate.payout.preview_skipped_min", { n: sk.below_min || 0 }) }}</li>
                            <li>
                                {{ $t("marketing.affiliate.payout.preview_skipped_profile", { n: sk.profile_not_verified || 0 }) }}
                                <router-link v-if="sk.profile_not_verified" :to="{ path: '/marketing/affiliate/members', query: { profile_status: 'submitted' } }" class="ml-1">→</router-link>
                            </li>
                            <li>{{ $t("marketing.affiliate.payout.preview_skipped_hold", { n: sk.on_hold || 0 }) }}</li>
                        </ul>
                    </template>
                </div>
                <div class="form-group mb-0">
                    <label class="form-control-label">{{ $t("marketing.affiliate.payout.note") }}</label>
                    <el-input v-model="note" type="textarea" :rows="2" maxlength="500" data-testid="aff-payout-note"></el-input>
                </div>
                <el-alert v-if="draftExistsId" type="warning" :closable="false" show-icon class="mt-3" :title="$t('marketing.affiliate.payout.draft_exists')">
                    <a href="#" data-testid="aff-open-draft" @click.prevent="goto(draftExistsId)">{{ $t("marketing.affiliate.payout.open_draft") }} →</a>
                </el-alert>
            </div>
            <span slot="footer">
                <base-button type="secondary" @click="createOpen = false">{{ $t("marketing.affiliate.cancel") }}</base-button>
                <base-button type="primary" :loading="creating" :disabled="!preview || !preview.eligible_count" data-testid="aff-payout-create-draft" @click="create">
                    {{ $t("marketing.affiliate.payout.create_draft") }}
                </base-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import { RadioGroup, RadioButton, Table, TableColumn, Tag, Alert, Pagination, Dialog, DatePicker, Input, Loading } from "element-ui";
import { mapGetters } from "vuex";
import { AffiliatePayoutService } from "@/resource";
import { apiErrorCode, isSchemaMissing } from "@/utils/marketing";
import { PAYOUT_STATUSES, bkkDate, payoutStatusTag, yesterday } from "@/utils/affiliate";
import affiliateMixin from "./components/affiliateMixin";

// การตลาด → Affiliate: รอบจ่ายเงิน (Phase 3 spec 2026-10-13 §5.5) — role affiliate_finance.
export default {
    name: "MarketingAffiliatePayouts",
    directives: { loading: Loading.directive },
    components: {
        [RadioGroup.name]: RadioGroup,
        [RadioButton.name]: RadioButton,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Tag.name]: Tag,
        [Alert.name]: Alert,
        [Pagination.name]: Pagination,
        [Dialog.name]: Dialog,
        [DatePicker.name]: DatePicker,
        [Input.name]: Input
    },
    mixins: [affiliateMixin],
    data() {
        return {
            loading: false,
            schemaMissing: false,
            rows: [],
            total: 0,
            page: 1,
            status: "",
            statuses: PAYOUT_STATUSES,
            createOpen: false,
            cutoff: yesterday(),
            note: "",
            preview: null,
            previewLoading: false,
            creating: false,
            draftExistsId: null,
            loadedOnce: false,
            pickerOptions: { disabledDate: (d) => bkkDate(d) > bkkDate(new Date()) }
        };
    },
    computed: {
        ...mapGetters({ canWrite: "Auth/canWriteAffiliateFinance" }),
        sk() {
            return (this.preview && this.preview.skipped) || {};
        }
    },
    created() {
        this.reload(1);
    },
    activated() {
        if (this.loadedOnce) this.reload();
    },
    methods: {
        statusTag: payoutStatusTag,
        async reload(page) {
            if (page) this.page = page;
            this.loading = true;
            try {
                const res = await AffiliatePayoutService.list({ status: this.status || undefined, page: this.page });
                const b = res.data || {};
                this.rows = b.data || [];
                this.total = b.total || 0;
                this.schemaMissing = false;
            } catch (err) {
                this.rows = [];
                if (isSchemaMissing(err)) this.schemaMissing = true;
                else this.showError(err);
            } finally {
                this.loading = false;
                this.loadedOnce = true;
            }
        },
        open(row) {
            this.goto(row.id);
        },
        goto(id) {
            this.createOpen = false;
            this.$router.push("/marketing/affiliate/payouts/" + id);
        },
        openCreate() {
            this.cutoff = yesterday();
            this.note = "";
            this.preview = null;
            this.draftExistsId = null;
            this.createOpen = true;
            this.loadPreview();
        },
        async loadPreview() {
            this.previewLoading = true;
            try {
                this.preview = this.unwrap(await AffiliatePayoutService.preview(this.cutoff));
            } catch (err) {
                this.preview = null;
                this.showError(err);
            } finally {
                this.previewLoading = false;
            }
        },
        async create() {
            this.creating = true;
            this.draftExistsId = null;
            try {
                const d = this.unwrap(await AffiliatePayoutService.create({ cutoff_date: this.cutoff, note: this.note.trim() }));
                this.notifyOk();
                this.goto(d && d.payout ? d.payout.id : "");
            } catch (err) {
                if (apiErrorCode(err) === "draft_exists") {
                    const body = (err.response && err.response.data) || {};
                    const fromBody = body.data && body.data.id;
                    const draft = this.rows.find((r) => r.status === "draft");
                    this.draftExistsId = fromBody || (draft && draft.id) || null;
                    if (!this.draftExistsId) {
                        try {
                            const res = await AffiliatePayoutService.list({ status: "draft", page: 1 });
                            const list = (res.data && res.data.data) || [];
                            this.draftExistsId = list.length ? list[0].id : null;
                        } catch (e) {
                            /* link stays hidden */
                        }
                    }
                    if (!this.draftExistsId) this.showError(err);
                } else this.showError(err);
            } finally {
                this.creating = false;
            }
        }
    }
};
</script>

<style scoped>
/* Verifier B: English headers broke mid-word (COMMISSIO/N) — the theme breaks
   anywhere; header cells wrap at spaces only. */
.aff-table >>> th .cell {
    word-break: normal;
    overflow-wrap: normal;
}

.aff-preview {
    min-height: 60px;
    background: #f6f9fc;
    border-radius: 6px;
    padding: 12px 16px;
}
.mk-aff-payouts >>> .mk-click-row {
    cursor: pointer;
}
</style>
